import { MutableRefObject, ReactNode, RefObject, useMemo } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardProps,
  CheckboxProps,
  Collapse,
  Divider,
  Group,
  ImageMantine,
  RadioCard,
  RadioGroupProps,
  RadioMantine,
  Stack,
  Text,
} from 'ui';

import { ShopToGiveTermsCheckboxCard } from '../../molecules/ShopToGiveTermsCheckboxCard/ShopToGiveTermsCheckboxCard';
import classes from './ShopToGiveEnrollmentForm.module.css';

type RewardsText = {
  subtitle: string;
  description: string;
  enrollTitle: string;
  enrollDescription: string;
  rejectTitle: string;
};

const REWARDS_TEXT: Record<'supporter' | 'fees', RewardsText> = {
  supporter: {
    subtitle: 'Do good without doing extra.',
    description:
      'Shop online or dine out as usual, and a portion of your purchase turns into Rewards—cash back for you or donations for your school.',
    enrollTitle: 'Yes, I want to support my organizations',
    enrollDescription: 'Link my card and accept the Terms to start earning Rewards',
    rejectTitle: "No, I don't want to support",
  },
  fees: {
    subtitle: 'Help your family save on school costs',
    description:
      'Your everyday online shopping and dining can earn your family Rewards. Apply Rewards to school fees, tickets, and other purchases—all right in the app.',
    enrollTitle: 'Yes, I want to start earning Rewards',
    enrollDescription: 'Link my card and accept the Terms to start earning Rewards',
    rejectTitle: 'No, I’ll skip Rewards for now',
  },
};

const ShopToGiveEnrollmentForm = ({
  isFeesUser,
  scrollerRef,
  containerProps,
  enrollmentOptionFieldProps,
  checkboxFieldProps,
  enrollmentOptionError,
  showAgreeToTermsCheckbox,
  agreeToTermsError,
}: {
  isFeesUser: boolean;
  scrollerRef?: RefObject<HTMLDivElement>;
  containerProps?: CardProps;
  enrollmentOptionFieldProps?: Omit<RadioGroupProps, 'children' | 'onChange'> & {
    onChange: (value?: boolean) => void;
  };
  checkboxFieldProps: CheckboxProps;
  showAgreeToTermsCheckbox?: boolean;
  enrollmentOptionError?: ReactNode;
  agreeToTermsError?: ReactNode;
}) => {
  const rewardsText = useMemo(() => {
    return isFeesUser ? REWARDS_TEXT.fees : REWARDS_TEXT.supporter;
  }, [isFeesUser]);
  return (
    <Card
      className={classes.ShopToGiveEnrollmentForm}
      ref={scrollerRef as MutableRefObject<HTMLDivElement>}
      {...containerProps}
    >
      <Box className={classes.ShopToGiveEnrollmentForm__header}>
        <Text fz="xl" fw="600">
          Join Givebacks Rewards
        </Text>
      </Box>

      <Stack className={classes.ShopToGiveEnrollmentForm__content}>
        <Group wrap="nowrap" gap="sm" align="flex-end">
          <Stack gap="lg">
            <Stack gap="sm">
              <Text c="gray.8" fw="600">
                {rewardsText.subtitle}
              </Text>
              <Text fz="sm" c="gray.8">
                {rewardsText.description}
              </Text>
            </Stack>
            <Box>
              <Button
                component="a"
                href="https://www.givebacks.com/how-it-works"
                target="_blank"
                variant="outline"
                size="sm"
                bg="white"
              >
                Learn More
              </Button>
            </Box>
          </Stack>
          <Divider size="1px" orientation="vertical" color="cobalt.1" />
          <Box className={classes.ShopToGiveEnrollmentForm__imageContainer}>
            <ImageMantine
              className={classes.ShopToGiveEnrollmentForm__image}
              src={
                'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/checkout/chart-checkout-support-givebacks.png'
              }
            />
          </Box>
        </Group>
      </Stack>
      <Stack className={classes.ShopToGiveEnrollmentForm__form}>
        <Alert.Collapse type="error" title={enrollmentOptionError} in={!!enrollmentOptionError} />

        <Text fz="sm" fw="600" c="gray.8">
          Select an option:
        </Text>

        <RadioMantine.Group
          {...enrollmentOptionFieldProps}
          value={
            // explicity allow an undefined value so that the radio group doesn't have a default value
            enrollmentOptionFieldProps?.value === undefined
              ? undefined
              : enrollmentOptionFieldProps?.value
                ? 'yes'
                : 'no'
          }
          onChange={(value: string) => {
            enrollmentOptionFieldProps?.onChange?.(value === 'yes');
          }}
          // Don't need error prop here because we have the Alert.Collapse above
          error={undefined}
        >
          <Stack>
            <RadioCard
              bg="mint.1"
              value="yes"
              title={rewardsText.enrollTitle}
              description={rewardsText.enrollDescription}
            />
            <RadioCard value="no" title={rewardsText.rejectTitle} />
          </Stack>
        </RadioMantine.Group>

        <Collapse in={!!showAgreeToTermsCheckbox}>
          <ShopToGiveTermsCheckboxCard
            hasError={!!agreeToTermsError}
            labelAppendage=" & link my card to Givebacks to earn free donations"
            checkboxFieldProps={checkboxFieldProps}
          />
        </Collapse>
      </Stack>
    </Card>
  );
};

export { ShopToGiveEnrollmentForm };
